import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import PermissionsSection from '@/components/PermissionsSection/PermissionsSection';
import FacilityPermissionsDropdown from '@/components/FacilityPermissionsDropdown/FacilityPermissionsDropdown';
import MirrorPermissionsDialog from './MirrorPermissionsDialog';
import { useMirrorPermissions } from '@/hooks/useMirrorPermissions';
import { PERMISSION_TYPES, PERMISSIONS_LEVEL } from '@/utility';

const UserPermissionsSection = ({
  permissions,
  onPermissionChange,
  organizations = [],
  selectedFacilities = [],
  permissionsType = PERMISSION_TYPES.PERMISSION,
  permissionLevel = PERMISSIONS_LEVEL.FACILITY,
  selectedRoleId = null,
  availableRoles = [],
  currentUser = null,
}) => {
  const { t } = useTranslation();
  const [applyToAllFacilities, setApplyToAllFacilities] = useState(true);
  const [selectedFacilityForConfig, setSelectedFacilityForConfig] = useState(
    '',
  );

  const handleApplyMirroredPermissions = (
    permissionsToApply,
    sourceUser,
    permissionType,
  ) => {
    if (permissionType === 'organization_permissions') {
      const orgId = Object.keys(permissions)[0];
      if (orgId) {
        onPermissionChange(permissionsToApply, orgId, 'organization');
      }
    } else {
      if (applyToAllFacilities) {
        selectedFacilities.forEach((facilityId) => {
          onPermissionChange(permissionsToApply, facilityId, 'facility');
        });
      } else {
        if (selectedFacilityForConfig) {
          onPermissionChange(
            permissionsToApply,
            selectedFacilityForConfig,
            'facility',
          );
        }
      }
    }

    toast.success(
      t('mirrorPermissions.appliedToForm', {
        sourceUserName: sourceUser?.name || sourceUser?.email,
      }),
    );
  };

  const {
    mirrorDialogOpen,
    mirrorPermissionType,
    openMirrorDialog,
    closeMirrorDialog,
    applyMirroredPermissions,
  } = useMirrorPermissions(handleApplyMirroredPermissions);

  // INFO: Auto-select first facility for configuration when not applying to all
  useEffect(() => {
    if (
      !applyToAllFacilities &&
      selectedFacilities.length > 0 &&
      !selectedFacilityForConfig
    ) {
      const firstFacilityId = selectedFacilities[0];
      setSelectedFacilityForConfig(firstFacilityId);
    }
  }, [applyToAllFacilities, selectedFacilities, selectedFacilityForConfig]);

  const checkPermissionsConsistency = useCallback(() => {
    if (selectedFacilities.length <= 1) return true;

    const facilityIds = selectedFacilities;
    const firstFacilityPermissions = permissions[facilityIds[0]] || {};

    for (let i = 1; i < facilityIds.length; i++) {
      const currentFacilityPermissions = permissions[facilityIds[i]] || {};

      if (
        JSON.stringify(firstFacilityPermissions) !==
        JSON.stringify(currentFacilityPermissions)
      ) {
        return false;
      }
    }
    return true;
  }, [selectedFacilities, permissions]);

  useEffect(() => {
    if (
      selectedFacilities.length > 1 &&
      permissionsType === PERMISSION_TYPES.PERMISSION &&
      permissionLevel === PERMISSIONS_LEVEL.FACILITY
    ) {
      const arePermissionsConsistent = checkPermissionsConsistency();
      setApplyToAllFacilities(arePermissionsConsistent);
    }
  }, [
    selectedFacilities,
    permissions,
    permissionsType,
    permissionLevel,
    checkPermissionsConsistency,
  ]);

  return (
    <Box>
      {permissionsType === PERMISSION_TYPES.ROLE && (
        <Alert severity='info' sx={{ mb: 3 }}>
          This user is assigned to a role.{' '}
          {permissionLevel === PERMISSIONS_LEVEL.ORGANIZATION
            ? 'Organization'
            : 'Facility'}{' '}
          permissions are inherited from the role.
          {selectedRoleId && availableRoles.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <strong>Selected Role:</strong>{' '}
              {availableRoles.find((r) => r.id === selectedRoleId)?.name}
            </Box>
          )}
        </Alert>
      )}

      {permissionsType === PERMISSION_TYPES.PERMISSION && (
        <>
          {permissionLevel === PERMISSIONS_LEVEL.ORGANIZATION && (
            <>
              {Object.keys(permissions).length > 0 ? (
                <>
                  {(() => {
                    const orgId = Object.keys(permissions)[0];
                    const organization = organizations?.find(
                      (org) => org.id === parseInt(orgId),
                    );
                    return (
                      <>
                        <Alert severity='info' sx={{ mb: 3 }}>
                          <Typography variant='body2'>
                            <strong>Organization:</strong>{' '}
                            {organization?.name || `ID: ${orgId}`}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            Permissions are set for this organization.
                          </Typography>
                        </Alert>

                        <PermissionsSection
                          permissions={permissions[orgId] || {}}
                          onPermissionChange={(newPermissions) =>
                            onPermissionChange(
                              newPermissions,
                              orgId,
                              'organization',
                            )
                          }
                          onApplyTemplate={(template) => {
                            const templatePermissions =
                              template.organizationPermissions ||
                              template.permissions;
                            onPermissionChange(
                              templatePermissions,
                              orgId,
                              'organization',
                            );
                          }}
                          permissionLevel='organization'
                          onMirrorClick={() =>
                            openMirrorDialog('organization_permissions')
                          }
                        />
                      </>
                    );
                  })()}
                </>
              ) : (
                <Alert severity='warning' sx={{ mb: 3 }}>
                  <Typography variant='body2'>
                    No organization selected. Please select an organization in
                    the Basic Information tab to configure permissions.
                  </Typography>
                </Alert>
              )}
            </>
          )}

          {permissionLevel === PERMISSIONS_LEVEL.FACILITY &&
            selectedFacilities.length > 0 && (
              <>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={applyToAllFacilities}
                        onChange={(e) =>
                          setApplyToAllFacilities(e.target.checked)
                        }
                        color='primary'
                      />
                    }
                    label={`Apply to all ${selectedFacilities.length} selected facilities`}
                  />
                </Box>

                {applyToAllFacilities ? (
                  <Alert severity='info' sx={{ mb: 2 }}>
                    These permissions will be applied to all{' '}
                    {selectedFacilities.length} selected facilities.
                  </Alert>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <FacilityPermissionsDropdown
                        selectedFacilities={selectedFacilities}
                        permissions={permissions}
                        selectedFacility={selectedFacilityForConfig}
                        onFacilityChange={setSelectedFacilityForConfig}
                      />

                      <Typography variant='body2' color='text.secondary'>
                        {
                          selectedFacilities.filter((facilityId) => {
                            return (
                              permissions[facilityId] &&
                              Object.keys(permissions[facilityId]).length > 0
                            );
                          }).length
                        }
                        /{selectedFacilities.length} facilities configured
                      </Typography>
                    </Box>
                  </>
                )}

                <PermissionsSection
                  permissions={
                    applyToAllFacilities
                      ? permissions[selectedFacilities[0]] || {}
                      : permissions[selectedFacilityForConfig] || {}
                  }
                  onPermissionChange={(newPermissions) => {
                    if (applyToAllFacilities) {
                      selectedFacilities.forEach((facilityId) => {
                        onPermissionChange(
                          newPermissions,
                          facilityId,
                          'facility',
                        );
                      });
                    } else {
                      onPermissionChange(
                        newPermissions,
                        selectedFacilityForConfig,
                        'facility',
                      );
                    }
                  }}
                  onApplyTemplate={(template) => {
                    const templatePermissions =
                      template.facilityPermissions || template.permissions;
                    if (applyToAllFacilities) {
                      selectedFacilities.forEach((facilityId) => {
                        onPermissionChange(
                          templatePermissions,
                          facilityId,
                          'facility',
                        );
                      });
                    } else {
                      onPermissionChange(
                        templatePermissions,
                        selectedFacilityForConfig,
                        'facility',
                      );
                    }
                  }}
                  permissionLevel='facility'
                  onMirrorClick={() => openMirrorDialog('facility_permissions')}
                />
              </>
            )}

          {permissionLevel === PERMISSIONS_LEVEL.FACILITY &&
            selectedFacilities.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant='body2' color='text.secondary'>
                  No facilities selected. Please select facilities in the Basic
                  Information section first.
                </Typography>
              </Box>
            )}
        </>
      )}

      {currentUser && (
        <MirrorPermissionsDialog
          open={mirrorDialogOpen}
          onClose={closeMirrorDialog}
          targetUser={currentUser}
          permissionType={mirrorPermissionType}
          organizationId={
            permissionLevel === PERMISSIONS_LEVEL.ORGANIZATION
              ? Object.keys(permissions)[0]
              : currentUser.organization_id
          }
          onApplyToForm={applyMirroredPermissions}
        />
      )}
    </Box>
  );
};

export default UserPermissionsSection;
